import { useState } from 'react';
import { Label } from '../UI/label';
import { Plus, X } from 'lucide-react';
import CustomSearchableDropdown from './CustomSearchableDD';

const ExecutiveTagsInput = ({
    fieldID,
    label,
    value = [], // Array of selected user IDs
    onChange,
    options = [], // Array of user objects
    placeholder = 'Select Executive',
    required = false,
    disabled = false,
    displayKey = 'label',
    valueKey = 'value'
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Get selected users from value array
    const selectedUsers = value
        .map(id => options.find(opt => opt[valueKey]?.toString() === id?.toString()))
        .filter(Boolean);

    // Get available options (exclude already selected + fixed users)
    const availableOptions = options.filter(opt => {
        const optValue = opt[valueKey]?.toString();
        // Exclude already selected users
        if (value.includes(optValue)) return false;
        // Only show users with ConnViewTypeID === 4 and not fixed
        return !opt.fixed && !opt.hiddenFromList;
    });

    const handleSelect = (selectedValue) => {
        if (!selectedValue) return;

        // Add new user to selection
        const newValue = [...value, selectedValue.toString()];
        onChange(newValue);
        setIsDropdownOpen(false);
    };

    const handleRemove = (userIdToRemove) => {
        if (disabled) return;

        // Check if this user is fixed (cannot be removed)
        const userToRemove = options.find(opt => opt[valueKey]?.toString() === userIdToRemove?.toString());
        if (userToRemove?.fixed) return;

        const newValue = value.filter(id => id?.toString() !== userIdToRemove?.toString());
        onChange(newValue);
    };

    const handleAddClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDropdownOpen(prev => !prev);
    };

    return (
        <div className="space-y-1.5">
            <Label htmlFor={fieldID} required={required}>
                {label}
            </Label>

            {/* Container with relative positioning for dropdown */}
            <div className="relative">
                {/* Tags display with Add button in same row */}
                <div className="flex flex-wrap gap-2 items-center">
                    {/* Render selected user tags */}
                    {selectedUsers.map((user) => {
                        const userId = user[valueKey];
                        const userName = user[displayKey];
                        const isFixed = user.fixed;

                        return (
                            <span
                                key={userId}
                                className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-card hover:bg-card/80 h-7 sahi-badge"
                            >
                                <span className="truncate max-w-[150px]">{userName}</span>
                                {/* Show X icon only if not fixed */}
                                {!isFixed && (
                                    <X
                                        className={`ml-2 h-4 w-4 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                        onClick={disabled ? undefined : () => handleRemove(userId)}
                                    />
                                )}
                            </span>
                        );
                    })}

                    {/* Add button tag - shown after last user tag, hidden when dropdown is open */}
                    {availableOptions.length > 0 && !disabled && !isDropdownOpen && (
                        <span
                            onClick={handleAddClick}
                            className="inline-flex items-center justify-center sahi-radius border px-2 py-0.5 cursor-pointer transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-card hover:bg-card/80 h-7 min-w-[28px] sahi-badge"
                            title="Add Executive"
                        >
                            <Plus className="h-4 w-4" />
                        </span>
                    )}
                </div>

                {/* Dropdown below tags - opens when + icon clicked */}
                {isDropdownOpen && availableOptions.length > 0 && (
                    <div className="mt-2 w-full">
                        <CustomSearchableDropdown
                            options={availableOptions}
                            value={null}
                            onSelect={(val) => {
                                if (val) handleSelect(val);
                                setIsDropdownOpen(false);
                            }}
                            placeholder={placeholder}
                            displayKey={displayKey}
                            valueKey={valueKey}
                            searchEnabled={true}
                            searchPlaceholder="Search users"
                            emptyMessage="No users available"
                            disabled={disabled}
                            openDialog={isDropdownOpen}
                            cancellable={true}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
export default ExecutiveTagsInput;